/**
 * The eleven roles from `program/Contracts` — "every permission names one of these as owner;
 * no others exist" — plus the twelfth, the sponsorship lead (ADR 0010), plus `platform_admin` (the person who runs the platform; canvas defect #31),
 * the public roles and the two non-person actors. A role is a template
 * of capabilities. A person gets a role through a role assignment (person × role × year × area),
 * never any other way.
 */
import { CAPABILITIES, CAPABILITY_IDS } from './capabilities.js';
import type { ChairKind, RoleId } from './registry.js';

export interface Role {
  id: RoleId;
  /** The number on the Contracts page, 1–11. Public and system roles have none. */
  n?: number;
  name: string;
  count: string;
  /** Second factor required before the first approval or signature. */
  secondFactor: boolean;
  /** Lane in: 1 password · 2 Microsoft (optional, on top of 1) · 3 magic link · 4 device PIN. */
  lane: 1 | 3 | 4 | 0;
  /** Which views the switcher lists. An area chair sees one and no switcher. */
  views: readonly ('volunteers' | 'money' | 'marketing' | 'ops' | 'chairs' | 'admin')[];
  /**
   * Doors this role may look through but not act on: `can()` grants them for reads only. A
   * director holds every door in every module this way, so the rail shows all of them, while
   * separation of duties (two signers, poster ≠ approver, counter ≠ sealer, granter ≠ grantee)
   * stays on the action lists.
   */
  readOnly?: readonly string[];
  home:
    | 'decide'
    | 'today'
    | 'admin'
    | 'money'
    | 'ops'
    | 'volunteers'
    | 'marketing'
    | 'portal'
    | 'device'
    | 'none';
  capabilities: readonly string[];
}

/** The area-chair set: "their area. Four things on a festival day, nothing required off-season." */
export const AREA_CHAIR_SET = [
  'volunteer.view_roster:my_areas',
  'volunteer.checkin',
  'volunteer.edit_signup:my_areas',
  'shift.propose',
  'roster.print',
  'area.brief.edit',
  'supply.request',
  // Money Part 4: what I paid for out of my own pocket. Mine, not my area's — a chair who
  // bought somebody else's ice is not argued with by a form.
  'expense.submit',
  'menu.edit:my_area',
  'radio.custody',
  'finance.view:my_area',
  // ADR 0030: an Ask on a line of my area, with a reason — the queue decides.
  'finance.budget.propose:my_area',
  // ADR 0036: Open a PO for my area — the third of the three things.
  'purchase.open:my_area',
  'safety.view',
  'msg.send:chairs_directors',
  'closeout.sign',
  // The January asks a chair was given (Sponsors Part 3): their own, and only theirs.
  'sponsor.ask.mine',
  // A gift in kind on its way to my area (Sponsors Part 4): the chair who takes delivery ticks it.
  'sponsor.gift.receive',
  'self.onboarding',
  'self.presence',
  // ADR 0060: who is this? — a worker on my roster who is in no family.
  'people.unmatched.resolve:my_areas',
] as const;

/**
 * Every role that signs in to a view hears its own live changes and reads its own bell
 * (`live.read`, Codex review #1 finding 4). Every one of those but the school searches what it may
 * open (`search.read`, P28): the school is the one outside party, held to its families (ADR 0029
 * (a)). A door, a device and the system have no session to hear them on.
 */
const R = (r: Role): Role => {
  if (r.views.length === 0) return r;
  const every = r.id === 'school' ? ['live.read'] : ['live.read', 'search.read'];
  const missing = every.filter((c) => !r.capabilities.includes(c));
  return missing.length ? { ...r, capabilities: [...r.capabilities, ...missing] } : r;
};

/**
 * The platform capabilities (ruling 91): integrations, sign-in rules, device enrollment and
 * revocation across the platform, role templates and grant overrides, second-factor reset,
 * system settings, break-glass configuration. A director does not hold these; the platform
 * admin does. The four with no screen yet are canvas defects #70–#73, not prunes.
 */
export const PLATFORM_SET: readonly string[] = [
  'platform.integrations.manage',
  'platform.signin.rules',
  'platform.devices.manage',
  'platform.roles.templates',
  'platform.settings',
  'platform.break_glass',
  'admin.second_factor.reset',
];

/**
 * Every door a person may hold — never a device's, the public's, a portal's or the system's.
 * A director holds all of these but the platform set, reads and actions (ruling 91); the
 * platform admin holds all of them. Separation of duties is per person, never per door.
 */
const EVERY_PERSON_DOOR: readonly string[] = CAPABILITIES.map((c) => c.id).filter(
  (id) =>
    !id.startsWith('system.') &&
    !id.startsWith('public.') &&
    !id.startsWith('portal.') &&
    id !== 'family.portal.view' &&
    id !== 'booth.operate' &&
    id !== 'door.admit',
);
const DIRECTOR_SET: readonly string[] = EVERY_PERSON_DOOR.filter(
  (id) => !PLATFORM_SET.includes(id),
);
const PLATFORM_ADMIN_SET: readonly string[] = EVERY_PERSON_DOOR;
/** The finance director: every door in the money module, plus the admin doors the template names. */
const MONEY_SET: readonly string[] = [
  ...CAPABILITIES.filter((c) => c.module === 'money').map((c) => c.id),
  // The vendor and payee pickers read people and organizations.
  'people.view',
];

export const ROLES: readonly Role[] = [
  R({
    id: 'director',
    n: 1,
    name: 'Director',
    count: '3–4 · year-round',
    secondFactor: true,
    lane: 1,
    views: ['volunteers', 'money', 'marketing', 'ops', 'chairs', 'admin'],
    home: 'decide',
    capabilities: DIRECTOR_SET,
  }),
  R({
    id: 'finance_director',
    n: 2,
    name: 'Finance director',
    count: '1 · chair-level, not a director',
    secondFactor: true,
    lane: 1,
    views: ['money', 'admin'],
    home: 'money',
    capabilities: [
      ...MONEY_SET,
      'pii.view_vendor_tax',
      'booth.device.enroll',
      'admin.audit.read',
      'self.onboarding',
    ],
  }),
  R({
    id: 'cash_change_chair',
    n: 3,
    name: 'Cash & change chair',
    count: '1 · festival weekend',
    secondFactor: false,
    lane: 1,
    views: ['chairs'],
    home: 'today',
    capabilities: [...AREA_CHAIR_SET, 'finance.pickup'],
  }),
  R({
    id: 'count_room_chair',
    n: 4,
    name: 'Count room chair',
    count: '1 · festival weekend',
    secondFactor: false,
    lane: 1,
    views: ['chairs'],
    home: 'today',
    capabilities: [...AREA_CHAIR_SET, 'finance.countroom'],
  }),
  R({
    id: 'volunteer_chair',
    n: 5,
    name: 'Volunteer chairs',
    count: '3+ · year-round',
    secondFactor: false,
    lane: 1,
    views: ['volunteers', 'money', 'marketing', 'ops', 'chairs', 'admin'],
    home: 'admin',
    capabilities: [
      'people.view',
      'people.edit',
      // ADR 0060: People → Unmatched workers, every area.
      'people.unmatched.resolve',
      'people.merge',
      'people.export_contact',
      'people.compliance.view',
      'people.compliance.record',
      'volunteer.verify_contact',
      'volunteer.settings.read',
      'volunteer.service_hours',
      // ADR 0034: the volunteer chairs make the festival's small forms.
      'form.make',
      'volunteer.edit_signup:all_areas',
      'volunteer.compliance.record',
      'shift.approve_proposal',
      'family.merge',
      'commitment.credit.run',
      'commitment.intent.view',
      'booth.device.enroll',
      'debrief.collect',
      'menu.copy.approve',
      'signage.proof.render',
      // R5: the volunteer office sells from the chair store (rd04).
      'commerce.chair.sell',
      'basket.intake',
      'raffle.operate',
      'ticket.comp',
      'finance.refund',
      'comms.send_email',
      'comms.send_sms',
      'comms.queue.retry',
      'admin.invite:area_chairs',
      'admin.view_as',
      'admin.access_issue',
      'meeting.record',
      'site.edit',
      'chair.pipeline',
      'self.onboarding',
    ],
  }),
  R({
    id: 'recruiting_chair',
    n: 6,
    name: 'Recruiting chair',
    count: '1 · within the volunteer chairs',
    secondFactor: false,
    lane: 1,
    views: ['volunteers', 'chairs'],
    home: 'volunteers',
    capabilities: [
      'people.view',
      'people.edit',
      ...AREA_CHAIR_SET,
      'volunteer.edit_signup:all_areas',
    ],
  }),
  R({
    id: 'dispatch',
    n: 7,
    name: 'Dispatch',
    count: '1 chair · festival days',
    secondFactor: false,
    lane: 1,
    views: ['ops', 'volunteers'],
    home: 'ops',
    capabilities: [
      'wt.operate',
      'wt.incident.open',
      'wt.incident.close',
      'wt.gate.report',
      'wt.radio.custody',
      'wt.weather.text',
      'wt.readiness.walk',
      'ops.grounds.edit',
      'supply.fulfill',
      'comms.push',
      'signage.screen.manage',
      'signage.proof.render',
      'self.onboarding',
    ],
  }),
  R({
    id: 'square_admin',
    n: 8,
    name: 'Square admin',
    count: 'James · Robert as backup',
    secondFactor: false,
    lane: 1,
    views: ['money'],
    home: 'money',
    capabilities: [
      'square.mapping.manage',
      'square.catalog.read',
      'ride.presale.manage',
      'self.onboarding',
    ],
    // The mapping is a list of Money's items and income accounts: read to map, never to post.
    readOnly: ['finance.view'],
  }),
  R({
    id: 'website_social',
    n: 9,
    name: 'Website & social',
    count: 'James · a social media chair',
    secondFactor: false,
    lane: 1,
    views: ['marketing'],
    home: 'marketing',
    capabilities: ['site.edit', 'comms.newsletter', 'self.onboarding'],
  }),
  R({
    id: 'area_chair',
    n: 10,
    name: 'Area chair',
    count: '~60 · their phase',
    secondFactor: false,
    lane: 1,
    views: ['chairs'],
    home: 'today',
    capabilities: [...AREA_CHAIR_SET],
  }),
  R({
    id: 'volunteer',
    n: 11,
    name: 'Volunteer',
    count: 'the public · magic link',
    secondFactor: false,
    lane: 3,
    views: [],
    home: 'portal',
    capabilities: ['public.door', 'public.signup', 'portal.myshifts'],
  }),
  // The twelfth registry role (James's ruling Sep 21, ADR 0010): a director-appointed chair who
  // owns the January asks, the sponsor records and artwork approval. Directors keep every door.
  R({
    id: 'sponsorship_lead',
    n: 12,
    name: 'Sponsorship lead',
    count: '1 · director-appointed · year-round',
    secondFactor: false,
    lane: 1,
    views: ['marketing'],
    home: 'marketing',
    capabilities: [
      'organization.view',
      'organization.edit',
      'organization.merge',
      'sponsor.manage',
      'sponsor.ask',
      'sponsor.ask.mine',
      'sponsor.artwork.approve',
      'sponsor.artwork.view',
      'sponsor.artwork.check',
      'sponsor.gift',
      'self.onboarding',
    ],
  }),
  R({
    /**
     * The designer (Sponsors Part 4, ADR 0016): the person who lays out the signage and the
     * program. Two screens — Artwork and Recognition — and nothing else in the festival: no
     * contact, no pledge, no payment, no person record. She checks a file against the list; a
     * director or the sponsorship lead approves it.
     */
    id: 'designer',
    name: 'Designer',
    count: '1–2 · director-appointed · year-round',
    secondFactor: false,
    lane: 1,
    views: ['marketing'],
    home: 'marketing',
    capabilities: ['sponsor.artwork.view', 'sponsor.artwork.check', 'self.onboarding'],
  }),
  R({
    /**
     * The school (ADR 0029, James Oct 13): a person the directors name, never more than two. One
     * section — The school's ledger — and nothing else in the festival: no person record, no
     * phone or email, no shift. A shortfall letter leaves only when a named school person
     * presses Send. It sets a second factor, like a director (ADR 0033, James Oct 14).
     */
    id: 'school',
    name: 'The school',
    count: '1–2 · named by the directors · year-round',
    secondFactor: true,
    lane: 1,
    views: ['volunteers'],
    home: 'volunteers',
    capabilities: ['commitment.ledger'],
  }),
  R({
    id: 'family',
    name: 'Family',
    count: 'the public · magic link',
    secondFactor: false,
    lane: 3,
    views: [],
    home: 'portal',
    capabilities: ['public.door', 'family.portal.view', 'public.signup', 'portal.myshifts'],
  }),
  R({
    id: 'sponsor',
    name: 'Sponsor',
    count: 'the public · magic link',
    secondFactor: false,
    lane: 3,
    views: [],
    home: 'portal',
    capabilities: ['public.door', 'portal.sponsor'],
  }),
  R({
    // A vendor's own page (Money Part 4). There is no vendor account: the link in the purchase
    // order's email is the whole of it, and this role exists only so the door has a name.
    id: 'vendor',
    name: 'Vendor',
    count: 'the businesses we buy from · a link, no password',
    secondFactor: false,
    lane: 3,
    views: [],
    home: 'portal',
    capabilities: ['public.door', 'portal.vendor'],
  }),
  R({
    // A responder (F16; James, Oct 23): EMS, police, fire or private security working the
    // festival, signed up at /alerts-signup for the four texts. No account, no door, never on a
    // volunteer roster; the role is only the record that they asked, and January removes it.
    id: 'responder',
    name: 'Responder',
    count: 'EMS · police · fire · security · texts only',
    secondFactor: false,
    lane: 0,
    views: [],
    home: 'none',
    capabilities: [],
  }),
  R({
    id: 'donor',
    name: 'Donor',
    count: 'the public · a receipt',
    secondFactor: false,
    lane: 3,
    views: [],
    home: 'portal',
    capabilities: ['public.donate', 'public.order'],
  }),
  R({
    // The twelfth: the person who runs the platform. Every door in every module, read and action —
    // and still one person: two hands stay two hands (a check, a plan, a count and its seal, a
    // grant). Every action taken through this role alone is a flagged audit row; the grant itself
    // is flagged and comes only from another director or from bootstrap. Never an `askRole`.
    id: 'platform_admin',
    name: 'Platform admin',
    count: '1 · James',
    secondFactor: true,
    lane: 1,
    views: ['volunteers', 'money', 'marketing', 'ops', 'chairs', 'admin'],
    home: 'admin',
    capabilities: PLATFORM_ADMIN_SET,
  }),
  R({
    id: 'device',
    name: 'Enrolled device',
    count: 'booths, count room, hall door, till',
    secondFactor: false,
    lane: 4,
    views: [],
    home: 'device',
    capabilities: ['booth.operate', 'door.admit'],
  }),
  R({
    id: 'system',
    name: 'The system',
    count: 'the worker, the clock, the feeds',
    secondFactor: false,
    lane: 0,
    views: [],
    home: 'none',
    capabilities: ['system.clock', 'system.engine', 'system.provider', 'system.feed'],
  }),
];

/** Kinds of area chair: the area-chair set plus the doors that kind needs. */
export const CHAIR_KIND_EXTRAS: Record<ChairKind, readonly string[]> = {
  general: [],
  ride: ['ride.view_redemption', 'ride.stock.count', 'ride.window.redeem'],
  // The raffle and basket chairs work the "will sell raffle tickets" and "will donate a basket" lists (Doors in Part 1).
  raffle: ['raffle.operate', 'commitment.intent.view'],
  basket: ['basket.intake', 'commitment.intent.view'],
  count_room: ['finance.countroom'],
  cash_change: ['finance.pickup'],
  dispatch: [
    'wt.operate',
    'wt.incident.open',
    'wt.gate.report',
    'wt.radio.custody',
    'wt.weather.text',
  ],
  supply: ['supply.fulfill'],
};

export function role(id: RoleId): Role {
  const r = ROLES.find((x) => x.id === id);
  if (!r) throw new Error(`unknown role '${id}'`);
  return r;
}

/** Every capability a role template names must exist in the registry. Checked by a test. */
export function unknownCapabilitiesInRoles(): string[] {
  const bad: string[] = [];
  for (const r of ROLES)
    for (const c of [...r.capabilities, ...(r.readOnly ?? [])])
      if (!CAPABILITY_IDS.has(c)) bad.push(`${r.id}: ${c}`);
  for (const [k, list] of Object.entries(CHAIR_KIND_EXTRAS))
    for (const c of list) if (!CAPABILITY_IDS.has(c)) bad.push(`chair kind ${k}: ${c}`);
  return bad;
}
